import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  notFound,
  useRouter,
  useStableCallback,
} from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { useMemo, useState } from "react"
import * as z from "zod"

import type {
  ExtendedClient,
  ExtendedProject,
} from "@repo/supabase/database-types"
import { Button } from "@repo/ui/components/ui/button"
import { If } from "@repo/ui/components/ui/if"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { getExtendedClientsQueryOptions } from "~/modules/clients/clients.queries"
import { ClientsSearchList } from "~/modules/clients/components/clients-search-list"
import { CreateProjectModal } from "~/modules/projects/components/create-project-modal"
import { ProjectsTable } from "~/modules/projects/components/projects-table"
import { getExtendedProjectsQueryOptions } from "~/modules/projects/projects.queries"
import type { Pagination } from "~/modules/resources/components/resource-invoices-table"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "projects-route",
})

const searchSchema = dataTableBaseSearchSchema.extend({
  be_slug: z.string().optional(),
  client_slug: z.string().optional(),
})

export const Route = createFileRoute("/_protected-layout/_os-layout/projects/")(
  {
    component: RouteComponent,
    validateSearch: searchSchema,
    loaderDeps: ({ search }) =>
      pick(search, [
        "be_slug",
        "client_slug",
        "q",
        "page",
        "page_size",
        "sorting",
        "columnFilters",
      ]),
    loader: async ({ context, deps }) => {
      context.queryClient
        .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
        .catch((error) => {
          log.error("Error getting extended business entities", { error })
          return null
        })

      const filters = pick(deps, [
        "q",
        "page",
        "page_size",
        "sorting",
        "columnFilters",
      ])

      return {
        filters,
      }
    },
  },
)

function RouteComponent() {
  const { isProjectCreationOn, isProjectManagementOn } = useFeatureConfigs()

  if (!isProjectManagementOn) {
    throw notFound()
  }

  const router = useRouter()
  const isMobile = useIsMobile()
  const { filters } = Route.useLoaderData()
  const { be_slug, client_slug } = Route.useSearch()
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false)

  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )

  const selectedBusinessEntity = useMemo(
    () =>
      businessEntitiesData.businessEntities.find(
        (entity) => entity.slug === be_slug,
      ) ?? null,
    [businessEntitiesData, be_slug],
  )

  const businessEntityFilterOptions = useMemo(() => {
    return businessEntitiesData.businessEntities.map((entity) => ({
      label: entity.name,
      value: entity.slug,
    }))
  }, [businessEntitiesData])

  const { data: clientsData, isLoading: isClientsLoading } = useQuery(
    getExtendedClientsQueryOptions({
      filters: {
        page: 1,
        page_size: 1000, // TODO: This should be replaced with an infinite scroll list (SearchList needs to be updated to support this)
        columnFilters: selectedBusinessEntity?.unit_id
          ? [
              {
                id: "business_entity_unit_ids",
                value: [selectedBusinessEntity.unit_id],
              },
            ]
          : undefined,
      },
    }),
  )

  const selectedClient = useMemo(() => {
    const selected = clientsData?.clients.find(
      (client) => client.slug === client_slug,
    )

    if (selected) {
      return selected
    }

    return null
  }, [clientsData, client_slug])

  const { data: projectsData } = useQuery(
    getExtendedProjectsQueryOptions({
      filters: {
        ...filters,
        columnFilters: [
          ...(filters.columnFilters ?? []),
          ...(selectedBusinessEntity?.unit_id
            ? [
                {
                  id: "business_entity_unit_ids",
                  value: [selectedBusinessEntity.unit_id],
                },
              ]
            : []),
          ...(selectedClient?.unit_id
            ? [{ id: "client_unit_ids", value: [selectedClient.unit_id] }]
            : []),
        ],
      },
    }),
  )

  const onSelectProject = (project: ExtendedProject) => {
    router.navigate({
      to: "/projects/$projectId/overview",
      params: { projectId: project.id },
      search: (prev) => prev,
    })
  }

  const onSelectBusinessEntity = (businessEntitySlug: string | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        be_slug: businessEntitySlug ?? undefined,
      }),
    })
  }

  const onSelectClient = (client: ExtendedClient | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        client_slug: client?.slug ?? undefined,
      }),
    })
  }

  const handleAddProject = useStableCallback(() => {
    setIsCreateProjectModalOpen(true)
  })

  const pagination: Pagination = {
    total: projectsData?.count ?? 0,
    page: filters.page,
    page_size: filters.page_size,
  }

  return (
    <PagePanel title="Projects">
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile && selectedClient,
          })}
        >
          <ClientsSearchList
            clients={clientsData?.clients ?? []}
            selectedClientSlug={selectedClient?.slug ?? null}
            onSelectClient={onSelectClient}
            filterOptions={businessEntityFilterOptions}
            filterValue={selectedBusinessEntity?.slug ?? null}
            setFilterValue={onSelectBusinessEntity}
            filterPlaceholder="Select a business entity"
            isLoading={isClientsLoading}
          />
        </div>

        <div
          className={cn("block flex-1 md:w-2/3 lg:w-3/4", {
            hidden: isMobile && !selectedClient,
          })}
        >
          <div className="h-full flex-1 overflow-auto">
            <If
              condition={projectsData}
              fallback={
                <div className="h-full flex-1 overflow-auto">
                  <div className="flex h-64 items-center justify-center">
                    <div className="text-white/70">Loading projects...</div>
                  </div>
                </div>
              }
            >
              {(data) => (
                <PageDetailsPanel
                  title={
                    selectedClient
                      ? `${selectedClient.name} Projects`
                      : "Projects"
                  }
                  actions={
                    <If condition={isProjectCreationOn}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleAddProject}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Project
                      </Button>
                    </If>
                  }
                >
                  <ProjectsTable
                    projects={data.projects}
                    onSelectProject={onSelectProject}
                    selectedClient={selectedClient ?? null}
                    pagination={pagination}
                    initialColumnFilters={filters.columnFilters}
                    initialSorting={filters.sorting}
                  />
                </PageDetailsPanel>
              )}
            </If>
          </div>
        </div>
      </div>

      <If condition={isProjectCreationOn}>
        <CreateProjectModal
          key={selectedClient?.id ?? "no-client"}
          open={isCreateProjectModalOpen}
          onOpenChange={setIsCreateProjectModalOpen}
          preselectedClientId={selectedClient?.id}
        />
      </If>
    </PagePanel>
  )
}
